import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ADMIN_SET_CPM_CAMPAIGN_VIDEO_EXCLUDED } from '@/queries/queries';

export interface ExcludedCampaignVideo {
  id: string;
  video_id: string;
  name?: string | null;
  thumbnail_url?: string | null;
}

interface ExcludedCampaignVideosProps {
  campaignId: string;
  videos: ExcludedCampaignVideo[];
  /** Called with the restored video's id. */
  onRestored?: (videoId: string) => void;
}

/**
 * Videos an admin removed from the campaign (from a card on the campaign
 * page). Restoring one makes it eligible again through its listing or channel.
 * Restored rows are hidden here rather than by refetching the campaign, which
 * would rehydrate the admin edit form and discard unsaved edits.
 */
export function ExcludedCampaignVideos({ campaignId, videos, onRestored }: ExcludedCampaignVideosProps) {
  const { t } = useTranslation();
  const [setExcluded] = useMutation(ADMIN_SET_CPM_CAMPAIGN_VIDEO_EXCLUDED);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoredIds, setRestoredIds] = useState<ReadonlySet<string>>(() => new Set());
  const remaining = videos.filter((video) => !restoredIds.has(video.id));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const restore = async (videoId: string) => {
    setRestoringId(videoId);
    try {
      await setExcluded({ variables: { campaignId, videoId, excluded: false } });
      setRestoredIds((current) => new Set(current).add(videoId));
      onRestored?.(videoId);
    } catch {
      setErrorMessage(t('admin.campaigns.edit.excludedVideos.error'));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t('admin.campaigns.edit.excludedVideos.help')}</p>
      {remaining.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('admin.campaigns.edit.excludedVideos.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {remaining.map((video) => (
            <li key={video.id} className="flex items-center gap-3 rounded-md border border-border/50 p-2">
              {video.thumbnail_url ? (
                <img src={video.thumbnail_url} alt="" className="h-10 w-16 shrink-0 rounded object-cover" />
              ) : (
                <div className="h-10 w-16 shrink-0 rounded bg-muted" />
              )}
              <span className="min-w-0 flex-1 truncate text-sm">{video.name || video.video_id}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={restoringId !== null}
                onClick={() => restore(video.id)}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('admin.campaigns.edit.excludedVideos.restore')}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.campaigns.edit.excludedVideos.errorTitle')}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
